import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import type { SandboxUser } from "sandbox/types";

import {
  identifySandboxUser,
  type PasswordResetSource,
  resetAmplitude,
  trackAmplitudeEvent,
  withTracking,
} from "sandbox/analytics/amplitude";
import { clearToken, storeToken } from "sandbox/api/client";
import {
  useResetConfirm,
  useResetRequest,
  useSignIn,
  useSignUp,
  useUpdatePassword,
} from "sandbox/api/hooks/useSandboxMutations";
import { STORAGE_KEYS } from "sandbox/constants";
import { resetSandboxState } from "sandbox/utils/resetState";

import {
  fromPasswordResetConfirm,
  fromPasswordResetRequest,
  fromSignIn,
  fromSignUpPardot,
  fromUpdatePassword,
  toAccessToken,
  toUser,
} from "./serializers";

interface SandboxAuthContextValue {
  isAuthenticated: boolean;
  requestPasswordReset: (
    email: string,
    source?: PasswordResetSource,
  ) => Promise<{ resetToken: string }>;
  resetPassword: (input: {
    token: string;
    newPassword: string;
  }) => Promise<void>;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signOut: () => void;
  signUp: (input: {
    firstName?: string;
    lastName?: string;
    fullName?: string;
    country: string;
    company: string;
    email: string;
    password: string;
    role?: string;
    agreedToTerms: boolean;
    marketingConsent?: boolean;
  }) => Promise<void>;
  signUpSalesforceReq: (input: {
    firstName: string;
    lastName: string;
    country: string;
    company: string;
    email: string;
    password: string;
    role?: string;
    agreedToTerms: boolean;
    marketingConsent?: boolean;
  }) => Promise<void>;
  updatePassword: (input: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<void>;
  user: SandboxUser | null;
}

const SandboxAuthContext = createContext<SandboxAuthContextValue | undefined>(
  undefined,
);

const readUser = (): SandboxUser | null => {
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEYS.user);
    return raw ? (JSON.parse(raw) as SandboxUser) : null;
  } catch {
    return null;
  }
};

const writeUser = (user: SandboxUser | null) => {
  if (user) {
    globalThis.localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  } else {
    globalThis.localStorage.removeItem(STORAGE_KEYS.user);
  }
};

export const SandboxAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<SandboxUser | null>(() => readUser());

  useEffect(() => {
    writeUser(user);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    identifySandboxUser({ company: user.company, userId: user.userId });
  }, [user]);

  const signInMutation = useSignIn();
  const signUpMutation = useSignUp();

  const signUpSalesforceReq = async (input: {
    firstName: string;
    lastName: string;
    country: string;
    company: string;
    email: string;
    password: string;
    role?: string;
    agreedToTerms: boolean;
    marketingConsent?: boolean;
  }) => {
    const url = import.meta.env.VITE_PARDOT_URL;
    if (!url) return;

    // Keys must match the form handler's External Field Names in Pardot
    const body = new URLSearchParams({
      "Work email": input.email,
      "First Name": input.firstName,
      "Last Name": input.lastName,
      Company: input.company,
      Country: input.country,
      "Job role": input.role ?? "",
      Consent: input.marketingConsent ? "Yes" : "No",
    });

    try {
      await fetch(url, { method: "POST", mode: "no-cors", body });
    } catch {
      // Do nothing, as this is a silent operation
    }
  };

  const signIn = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    await withTracking("Signed In", async () => {
      const loginResponse = await signInMutation.mutateAsync(
        fromSignIn({ email, password }),
      );
      storeToken(toAccessToken(loginResponse));
      const signedInUser = toUser(loginResponse);

      identifySandboxUser({
        company: signedInUser.company,
        userId: signedInUser.userId,
      });
      setUser(signedInUser);
    });
  };

  const signUp = async (input: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    country: string;
    company: string;
    email: string;
    password: string;
    role?: string;
    agreedToTerms: boolean;
    marketingConsent?: boolean;
  }) => {
    await withTracking(
      "Signed Up",
      async () => {
        const signUpResponse = await signUpMutation.mutateAsync(
          fromSignUpPardot(input),
        );

        identifySandboxUser({
          company: signUpResponse?.company_name,
          userId: signUpResponse?.user_id,
        });
      },
      {
        country: input.country,
        marketing_consent: Boolean(input.marketingConsent),
      },
    );
  };

  const signOut = () => {
    clearToken();
    resetSandboxState();
    trackAmplitudeEvent("Signed Out", {});
    resetAmplitude();
    setUser(null);
  };

  const resetRequestMutation = useResetRequest();

  const requestPasswordReset = async (
    email: string,
    source: PasswordResetSource = "forgot_password",
  ) => {
    await withTracking(
      "Password Reset Requested",
      () => resetRequestMutation.mutateAsync(fromPasswordResetRequest(email)),
      { source },
    );
    const resetToken = `rst_${Math.random().toString(36).slice(2, 14)}`;
    return { resetToken };
  };

  const resetConfirmMutation = useResetConfirm();

  const resetPassword = async ({
    token,
    newPassword,
  }: {
    token: string;
    newPassword: string;
  }) => {
    await withTracking("Password Reset Completed", () =>
      resetConfirmMutation.mutateAsync(
        fromPasswordResetConfirm({ token, newPassword }),
      ),
    );
  };

  const updatePasswordMutation = useUpdatePassword();

  const updatePassword = async (input: {
    currentPassword: string;
    newPassword: string;
  }) => {
    await withTracking("Password Changed", () =>
      updatePasswordMutation.mutateAsync(
        fromUpdatePassword({
          currentPassword: input.currentPassword,
          newPassword: input.newPassword,
        }),
      ),
    );
  };

  const value: SandboxAuthContextValue = {
    user,
    isAuthenticated: user !== null,
    signIn,
    signUp,
    signUpSalesforceReq,
    signOut,
    requestPasswordReset,
    resetPassword,
    updatePassword,
  };

  return (
    <SandboxAuthContext.Provider value={value}>
      {children}
    </SandboxAuthContext.Provider>
  );
};

export const useSandboxAuth = () => {
  const ctx = useContext(SandboxAuthContext);
  if (!ctx) {
    throw new Error("useSandboxAuth must be used inside SandboxAuthProvider");
  }
  return ctx;
};
